import { Factory, Layers, FlaskConical, Boxes, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";

const SERVICES = [
  { icon: Factory, title: "Third-Party Manufacturing", desc: "Manufacture your existing veterinary formulations under your brand with full pharmaceutical compliance and documentation." },
  { icon: Layers, title: "Private Label Manufacturing", desc: "Choose from our validated formulations and launch under your own brand with custom artwork, pack sizes and export documents." },
  { icon: FlaskConical, title: "Custom Formulation Development", desc: "Partner with our R&D team to develop bespoke veterinary formulations — from concept and prototyping to scale-up and stability." },
  { icon: Boxes, title: "Packaging Solutions", desc: "Bottles, sachets, jars, drums, secondary cartons, master cartons — custom-built for your market and shipping requirements." },
];

const BENEFITS = [
  "GMP, ISO 9001:2015 & WHO-GMP aligned facility",
  "Liquid, powder, granulation & semi-solid capabilities",
  "Validated SOPs and batch documentation",
  "Export documentation: COO, COPP, Free Sale Certificate",
  "Confidentiality and IP protection assured",
  "MOQ flexibility for new brands and pilot launches",
];

export default function ContractManufacturing() {
  return (
    <>
      <SEO title="Contract Manufacturing" description="Forage offers third-party veterinary manufacturing, private label production, custom formulation development and packaging solutions to brands and exporters." path="/contract-manufacturing" />

      {/* Header */}
      <section className="bg-[#0B2545] text-white py-24 px-6 md:px-12 lg:px-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container-fr relative">
          <span className="overline text-[#2A9D8F]">Contract Manufacturing</span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-3 leading-tight max-w-3xl">
            Your formulation. Our facility. Pharmaceutical-grade output.
          </h1>
          <p className="text-lg text-slate-300 mt-6 max-w-2xl">
            Trusted by veterinary brands, exporters and institutional buyers
            for confidential third-party manufacturing, private label
            production and custom development.
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-white">
        <div className="container-fr">
          <div className="max-w-3xl">
            <span className="overline">What We Offer</span>
            <h2 className="h-section mt-3">Four ways to partner with Forage.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {SERVICES.map((s, i) => (
              <div key={s.title} className="p-8 rounded-xl bg-[#F8FAFC] border border-slate-200 card-lift fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-12 h-12 rounded-lg bg-[#0B2545] text-[#2A9D8F] flex items-center justify-center mb-5">
                  <s.icon size={22} />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#0B2545]">{s.title}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-[#0B2545] text-white">
        <div className="container-fr grid lg:grid-cols-2 gap-14 items-center">
          <img src="https://images.unsplash.com/photo-1717386255773-1e3037c81788?auto=format&fit=crop&w=1200&q=80" alt="Manufacturing partner" loading="lazy" className="rounded-xl object-cover w-full h-[460px] shadow-2xl" />
          <div>
            <span className="overline text-[#2A9D8F]">Why Partner With Forage</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
              Manufacture with confidence. Launch with speed.
            </h2>
            <ul className="mt-8 space-y-4">
              {BENEFITS.map((b) => (
                <li key={b} className="flex gap-3 items-start text-slate-200">
                  <CheckCircle2 className="text-[#2A9D8F] flex-shrink-0 mt-0.5" size={18} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-fr max-w-3xl">
          <span className="overline">Partner Enquiry</span>
          <h2 className="h-section mt-3">Let's discuss your manufacturing project.</h2>
          <p className="text-slate-600 mt-4">
            Share your product, volumes and timeline. We sign NDAs upfront, so
            you can speak freely.
          </p>
          <div className="mt-10 bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <ContactForm
              defaultType="contract"
              defaultSubject="Contract Manufacturing Enquiry"
              defaultMessage="We are interested in contract manufacturing with Forage. Product: ____. Estimated monthly volume: ____. Markets: ____. Please share NDA and capability deck."
              testIdPrefix="contract"
            />
          </div>
        </div>
      </section>
    </>
  );
}
